import type { Node } from '@xyflow/react'
import { isPassthroughType } from '@/features/backtest/utils/inline-passthroughs'
import {
  shiftedAnchor,
  type CardAnchor,
  frameRect,
  makeRoomForResize,
  nodeRect,
  recordRoom,
  type MadeRoom,
} from '@/features/backtest/utils/make-room'

/** Shared geometry for group chrome — used by Create Group and fit-on-drop. */
export const GROUP_PADDING = 20
export const GROUP_TOP_PADDING = 80
// Gutter reserved on each side for the I/O pills. Sized to fit a pill at its
// widest (a renamed label truncates at max-w-24 → ~146px pill) plus the
// output's right-anchor offset — see IO_PILL_WIDTH below. Tightened from 230
// once the pills became auto-width and compact.
export const IO_GUTTER = 170
export const IO_PILL_SPACING = 44
export const IO_PILL_HEIGHT = 30
export const IO_PILL_X = 14

const DEFAULT_NODE_WIDTH = 240
const DEFAULT_NODE_HEIGHT = 180

/**
 * Pills are auto-width (they hug their content). This is the pre-measure
 * fallback / the slot reserved when right-anchoring an output pill at
 * creation — the widest a truncated pill gets, so outputs sit near the
 * group's right border and stay inside IO_GUTTER (= IO_PILL_WIDTH + 24).
 */
export const IO_PILL_WIDTH = 146

/**
 * Re-fit the frames a batch of canvas changes touched (Chris, 2026-10-07:
 * a frame fits around its nodes): a subnet made or expanded this session
 * (`pending`) once its nodes report their measured sizes, and any subnet one
 * of whose nodes was deleted. A strategy only opened keeps its saved layout,
 * so opening one never marks it unsaved. `before` is the graph the changes
 * were applied to (a deleted node's parent is read there). Returns `after`
 * itself when nothing re-fits. `openSubnetId`, the subnet open on the full
 * canvas, fits without making room: the strategy around it is hidden, and
 * closing it does not release a record, so a push there would never come back.
 */
export function fitAfterChanges<N extends Node>(
  before: N[],
  after: N[],
  changes: ReadonlyArray<{ type: string; id?: string }>,
  pending: ReadonlySet<string>,
  openSubnetId: string | null = null,
): N[] {
  const parentOf = (nodes: N[], id: string | undefined) =>
    id === undefined ? undefined : nodes.find((n) => n.id === id)?.parentId
  const frames = new Set<string>()
  const late = new Set<string>()
  for (const change of changes) {
    if (change.type === 'dimensions') {
      const parent = parentOf(after, change.id)
      if (parent && pending.has(parent)) {
        frames.add(parent)
        late.add(parent)
      }
    } else if (change.type === 'remove') {
      const parent = parentOf(before, change.id)
      if (parent) frames.add(parent)
    }
  }
  let next = followFrameGrowth(before, after, changes)
  for (const frame of frames) {
    const fitted = fitSubnetToChildren(frame, next) as N[]
    // A frame that grows or moves once its nodes are measured makes room for itself, and
    // the shifts join the subnet's record so a collapse still reverses them exactly.
    const from = next.find((n) => n.id === frame)
    const to = fitted.find((n) => n.id === frame)
    if (late.has(frame) && frame !== openSubnetId) {
      let moved = [frame]
      if (fitted !== next && from && to) {
        const pushed = makeRoomForResize(fitted, frame, frameRect(from), frameRect(to))
        next = recordRoom(pushed.nodes, frame, pushed.shifts)
        moved = [frame, ...Object.keys(pushed.shifts)]
      }
      // A group around the frame grows to hold it and what it pushed, on the same record,
      // even when the fit changed nothing: React Flow may have grown the frame itself.
      next = growGroupsToHold(next, frame, moved)
    } else {
      next = fitted
    }
  }
  return next
}

/**
 * React Flow moves a frame with no `dragging` flag when it grows it up or left
 * around a child (expandParent; a pill first measured while seated half over
 * the border does it on the first expand after a load). Nobody moved the
 * subnet, so its collapse anchor follows the frame, as it follows a fit;
 * restoreCard read the move as a drag and put the card down that far off.
 */
function followFrameGrowth<N extends Node>(
  before: N[],
  after: N[],
  changes: ReadonlyArray<{ type: string; id?: string }>,
): N[] {
  let next = after
  for (const change of changes) {
    if (change.type !== 'position' || 'dragging' in change) continue
    const now = next.find((n) => n.id === change.id)
    const was = before.find((n) => n.id === change.id)
    const anchor = now?.data?.cardAnchor as CardAnchor | undefined
    if (now?.type !== 'subnet' || !was || !anchor) continue
    const dx = now.position.x - was.position.x
    const dy = now.position.y - was.position.y
    if (dx === 0 && dy === 0) continue
    next = next.map((n) =>
      n.id === now.id
        ? { ...n, data: { ...n.data, cardAnchor: shiftedAnchor(anchor, dx, dy) } }
        : n,
    )
  }
  return next
}

/**
 * One group grown to hold a subnet expanded inside it, as the subnet's
 * collapse undoes it: how far the group's origin moved (its members moved back
 * by as much, keeping their canvas positions), its size before and after, and
 * the group's own neighbours pushed clear. Kept in order on the subnet's
 * `data.grewGroups`.
 */
export interface GroupGrowth {
  id: string
  dx: number
  dy: number
  before: { width: number; height: number }
  after: { width: number; height: number }
  pushed: MadeRoom
}

const sameSize = (a: { width: number; height: number }, b: { width: number; height: number }) =>
  Math.abs(a.width - b.width) < 0.5 && Math.abs(a.height - b.height) < 0.5

/**
 * Grow the group a subnet sits in, and each group around that one, so the
 * nodes that just moved in it (`moved`: the expanded frame and the members it
 * pushed) stay inside (Chris, 2026-11-01: an expanded subnet spilled over its
 * group's border). Only a side they cross moves, out to clear them by the
 * padding Create Group uses; the group's own neighbours are pushed clear as a
 * frame's are, and the next group out holds the grown group and what it
 * pushed. Groups never shrink here. Each growth joins the subnet's
 * `data.grewGroups`, so its collapse gives it back (`shrinkGroupsBack`).
 * Returns `nodes` itself when no group grows.
 */
export function growGroupsToHold<N extends Node>(
  nodes: N[],
  subnetId: string,
  moved: Iterable<string>,
): N[] {
  const steps: GroupGrowth[] = []
  let next = nodes
  let held = new Set(moved)
  let groupId = nodes.find((n) => n.id === subnetId)?.parentId
  while (groupId) {
    const group = next.find((n) => n.id === groupId)
    if (group?.type !== 'group') break
    const before = frameRect(group)
    let left = 0
    let top = 0
    let right = before.width
    let bottom = before.height
    for (const n of next) {
      if (n.parentId !== groupId || !held.has(n.id) || n.hidden) continue
      const r = n.type === 'subnet' || n.type === 'group' ? frameRect(n) : nodeRect(n)
      if (r.x < 0) left = Math.min(left, r.x - GROUP_PADDING)
      if (r.y < 0) top = Math.min(top, r.y - GROUP_TOP_PADDING)
      if (r.x + r.width > before.width) right = Math.max(right, r.x + r.width + GROUP_PADDING)
      if (r.y + r.height > before.height) {
        bottom = Math.max(bottom, r.y + r.height + GROUP_PADDING)
      }
    }
    const width = right - left
    const height = bottom - top
    if (left === 0 && top === 0 && sameSize(before, { width, height })) break
    const id = groupId
    const grown = next.map((n) => {
      if (n.id === id) {
        return {
          ...n,
          position: { x: n.position.x + left, y: n.position.y + top },
          width,
          height,
          style: { ...n.style, width, height },
          measured: { width, height },
        }
      }
      if (n.parentId !== id || (left === 0 && top === 0)) return n
      return { ...n, position: { x: n.position.x - left, y: n.position.y - top } }
    })
    const pushed = makeRoomForResize(grown, id, before, frameRect(grown.find((n) => n.id === id)!))
    next = pushed.nodes
    steps.push({
      id,
      dx: left,
      dy: top,
      before: { width: before.width, height: before.height },
      after: { width, height },
      pushed: pushed.shifts,
    })
    held = new Set([id, ...Object.keys(pushed.shifts)])
    groupId = group.parentId
  }
  if (steps.length === 0) return nodes
  return next.map((n) => {
    if (n.id !== subnetId) return n
    const record = (n.data?.grewGroups as GroupGrowth[] | undefined) ?? []
    return { ...n, data: { ...n.data, grewGroups: [...record, ...steps] } }
  })
}

/**
 * Undo every growth recorded on the subnet, newest first: each group's origin
 * and size go back (its members keep their canvas positions) and its pushed
 * neighbours return. A group resized by hand since is left as it is, with its
 * neighbours; a group or neighbour that is gone is skipped. Always drops the
 * record.
 */
export function shrinkGroupsBack<N extends Node>(nodes: N[], subnetId: string): N[] {
  const subnet = nodes.find((n) => n.id === subnetId)
  const steps = subnet?.data?.grewGroups as GroupGrowth[] | undefined
  if (!subnet || !steps) return nodes
  let next = nodes.map((n) => {
    if (n.id !== subnetId) return n
    const { grewGroups: _dropped, ...data } = n.data
    return { ...n, data }
  })
  for (const step of [...steps].reverse()) {
    const group = next.find((n) => n.id === step.id)
    if (!group || !sameSize(frameRect(group), step.after)) continue
    const { width, height } = step.before
    next = next.map((n) => {
      if (n.id === step.id) {
        return {
          ...n,
          position: { x: n.position.x - step.dx, y: n.position.y - step.dy },
          width,
          height,
          style: { ...n.style, width, height },
          measured: { width, height },
        }
      }
      if (n.parentId === step.id && (step.dx !== 0 || step.dy !== 0)) {
        return { ...n, position: { x: n.position.x + step.dx, y: n.position.y + step.dy } }
      }
      const shift = step.pushed[n.id]
      if (!shift || n.parentId !== group.parentId) return n
      return { ...n, position: { x: n.position.x - shift.dx, y: n.position.y - shift.dy } }
    })
  }
  return next
}

/**
 * Fit an EXPANDED subnet's frame around its nodes, and seat its I/O pills on
 * its border like ports: inputs centred on the left edge, outputs on the
 * right (Chris, 2026-10-07: "limit the size to fit around the nodes"; the
 * frame used to wrap the pills too, placed 170px out at creation, so it never
 * hugged). A side with pills is padded by half the widest pill, so a pill
 * never covers a node. Nodes (reroutes included) keep their canvas positions;
 * a pill keeps its height, clamped inside the frame, pills on one side kept
 * apart, the frame growing to hold them. Returns the input array unchanged
 * (same reference) when nothing needs to move, so callers can skip a state
 * update.
 */
export function fitSubnetToChildren(groupId: string, nodes: Node[]): Node[] {
  const groupNode = nodes.find((n) => n.id === groupId && n.type === 'subnet')
  if (!groupNode || groupNode.data?.collapsed === true) return nodes

  const children = nodes.filter((n) => n.parentId === groupId)
  const isPill = (n: Node) => n.type === 'subnetInput' || n.type === 'subnetOutput'
  const members = children.filter((n) => !isPill(n))
  // A group holding only pills (or reroutes) is degenerate: leave it alone
  if (!members.some((n) => !isPassthroughType(n.type))) return nodes

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const n of members) {
    const w = n.measured?.width ?? DEFAULT_NODE_WIDTH
    const h = n.measured?.height ?? DEFAULT_NODE_HEIGHT
    minX = Math.min(minX, n.position.x)
    minY = Math.min(minY, n.position.y)
    maxX = Math.max(maxX, n.position.x + w)
    maxY = Math.max(maxY, n.position.y + h)
  }

  const pillWidth = (n: Node) => n.measured?.width ?? IO_PILL_WIDTH
  const pillHeight = (n: Node) => n.measured?.height ?? IO_PILL_HEIGHT
  const inputs = children.filter((n) => n.type === 'subnetInput')
  const outputs = children.filter((n) => n.type === 'subnetOutput')
  const halfWidest = (pills: Node[]) =>
    pills.length > 0 ? Math.max(...pills.map(pillWidth)) / 2 : 0
  const leftPad = GROUP_PADDING + halfWidest(inputs)
  const rightPad = GROUP_PADDING + halfWidest(outputs)

  const width = maxX - minX + leftPad + rightPad
  let height = maxY - minY + GROUP_TOP_PADDING + GROUP_PADDING
  // How far the group's origin moves, in (former) relative coordinates
  const shiftX = minX - leftPad
  const shiftY = minY - GROUP_TOP_PADDING

  // Each pill on its border, at its own height, inside the frame and clear
  // of the pill above it on that side.
  const seated = new Map<string, { x: number; y: number }>()
  const seat = (pills: Node[], edgeX: number) => {
    let floor = GROUP_TOP_PADDING
    const byHeight = [...pills].sort((a, b) => a.position.y - b.position.y)
    for (const pill of byHeight) {
      const lowest = height - GROUP_PADDING - pillHeight(pill)
      const y = Math.max(floor, Math.min(pill.position.y - shiftY, lowest))
      seated.set(pill.id, { x: edgeX - pillWidth(pill) / 2, y })
      floor = y + Math.max(IO_PILL_SPACING, pillHeight(pill))
      height = Math.max(height, y + pillHeight(pill) + GROUP_PADDING)
    }
  }
  seat(inputs, 0)
  seat(outputs, width)

  // The size the frame is drawn at: React Flow draws width/height over style, and
  // expandParent writes only those, so a frame it widened still fits back.
  const current = frameRect(groupNode)
  const currentWidth = current.width
  const currentHeight = current.height
  const same = (a: number, b: number) => Math.abs(a - b) < 0.5
  const pillsInPlace = [...seated].every(([id, at]) => {
    const pill = children.find((n) => n.id === id)!
    return same(pill.position.x, at.x) && same(pill.position.y, at.y)
  })
  // A subnet once collapsed keeps the size it had then (`expandedSize`), and
  // applyCollapseState sizes an expanded frame from it: left stale, the next
  // projection undid this fit and the nodes spilled out of the frame.
  const expandedSize = groupNode.data?.expandedSize as { width: number; height: number } | undefined
  const expandedInStep =
    !expandedSize || (same(expandedSize.width, width) && same(expandedSize.height, height))
  if (
    same(shiftX, 0) &&
    same(shiftY, 0) &&
    same(width, currentWidth) &&
    same(height, currentHeight) &&
    pillsInPlace &&
    expandedInStep
  ) {
    return nodes
  }

  return nodes.map((n) => {
    if (n.id === groupId) {
      return {
        ...n,
        position: { x: n.position.x + shiftX, y: n.position.y + shiftY },
        style: { ...n.style, width, height },
        ...(n.width !== undefined ? { width } : {}),
        ...(n.height !== undefined ? { height } : {}),
        ...(expandedSize || n.data?.cardAnchor
          ? {
              data: {
                ...n.data,
                ...(expandedSize ? { expandedSize: { width, height } } : {}),
                // The frame's own move is not a user drag: keep the collapse anchor in step.
                ...(n.data?.cardAnchor
                  ? { cardAnchor: shiftedAnchor(n.data.cardAnchor as CardAnchor, shiftX, shiftY) }
                  : {}),
              },
            }
          : {}),
      }
    }
    if (n.parentId !== groupId) return n
    const at = seated.get(n.id)
    if (at) return same(at.x, n.position.x) && same(at.y, n.position.y) ? n : { ...n, position: at }
    if (same(shiftX, 0) && same(shiftY, 0)) return n
    return { ...n, position: { x: n.position.x - shiftX, y: n.position.y - shiftY } }
  })
}

/** Room reserved around the content when sizing a subnet workspace. */
export const WORKSPACE_MARGIN = 200

/**
 * Resting minimum the subnet frame settles to when you leave the editing
 * takeover — big enough that a sparse subnet never snaps to a cramped box, but
 * smaller than the open-for-editing workspace. The frame never goes below this.
 */
export const SUBNET_RESTING_MIN_WIDTH = 720
export const SUBNET_RESTING_MIN_HEIGHT = 480

/**
 * Size an EXPANDED subnet's frame to `max(content + margin, min)`, centered on
 * its content. Used both ways: a viewport-sized min on OPEN gives a roomy
 * editing workspace; a smaller resting min on CLOSE settles it to a tidy floor
 * (it can shrink toward the min, but never below it). Children keep their
 * absolute canvas positions — the frame moves/resizes around them, the origin
 * shifts, and child relative coords compensate. Returns the same array
 * reference when the frame already matches the target.
 */
export function growSubnetForEditing(
  groupId: string,
  nodes: Node[],
  minWidth: number,
  minHeight: number,
): Node[] {
  const groupNode = nodes.find((n) => n.id === groupId && n.type === 'subnet')
  if (!groupNode || groupNode.data?.collapsed === true) return nodes

  const children = nodes.filter((n) => n.parentId === groupId)
  if (!children.some((n) => !isPassthroughType(n.type))) return nodes

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const n of children) {
    const isPill = isPassthroughType(n.type)
    const w = n.measured?.width ?? (isPill ? IO_PILL_WIDTH : DEFAULT_NODE_WIDTH)
    const h = n.measured?.height ?? (isPill ? IO_PILL_HEIGHT : DEFAULT_NODE_HEIGHT)
    minX = Math.min(minX, n.position.x)
    minY = Math.min(minY, n.position.y)
    maxX = Math.max(maxX, n.position.x + w)
    maxY = Math.max(maxY, n.position.y + h)
  }

  const width = Math.max(maxX - minX + 2 * WORKSPACE_MARGIN, minWidth)
  const height = Math.max(maxY - minY + 2 * WORKSPACE_MARGIN, minHeight)
  // Move the origin so the content's center lands at the frame's center.
  const shiftX = (minX + maxX) / 2 - width / 2
  const shiftY = (minY + maxY) / 2 - height / 2

  const currentWidth = Number(groupNode.style?.width ?? groupNode.measured?.width ?? 0)
  const currentHeight = Number(groupNode.style?.height ?? groupNode.measured?.height ?? 0)
  const unchanged = (a: number, b: number) => Math.abs(a - b) < 0.5
  if (
    unchanged(shiftX, 0) &&
    unchanged(shiftY, 0) &&
    unchanged(width, currentWidth) &&
    unchanged(height, currentHeight)
  ) {
    return nodes
  }

  return nodes.map((n) => {
    if (n.id === groupId) {
      return {
        ...n,
        position: { x: n.position.x + shiftX, y: n.position.y + shiftY },
        width,
        height,
        style: { ...n.style, width, height },
        ...(n.data?.cardAnchor
          ? {
              data: {
                ...n.data,
                cardAnchor: shiftedAnchor(n.data.cardAnchor as CardAnchor, shiftX, shiftY),
              },
            }
          : {}),
      }
    }
    if (n.parentId !== groupId) return n
    return { ...n, position: { x: n.position.x - shiftX, y: n.position.y - shiftY } }
  })
}
